import React from 'react';
import './Dancers.css';
import Timeline from '../Timeline/Timeline';
import StageCanvas, { StageCanvasMode } from '../StageCanvas/StageCanvas';
import useTypedSelector from '../../hooks/use-type-selectors';
import { Units } from '../../models/Settings';
import MeasureInput from '../MeasureInput';
import useActions from '../../hooks/use-actions';
import { DancerPathColor, DancerPathNodeShape, IDancer, dancerDefaultSpeed } from '../../models/Timeline';
import { centimetersToFeetRounded, centimetersToMetersRounded, feetToCentimeters, metersToCentimeters } from '../../utils/general';
import { Vec2 } from '../../utils/vec2';
//import '../../App.css'

const Dancers: React.FC = () => {
    const { addDancer, setSelectedDancer, deleteDancer, setDancerPathAppearance, setDancerHeight, addDancerPathNode, setDancerPathNode, setDancerPathNodeDuration, deleteDancerPathNode, setDancerName } = useActions();
    const settings = useTypedSelector(state => state.settings);
    const timeline = useTypedSelector(state => state.undoable.timeline);

    function giveScreenReaderFeedback(text: string) {
        const feedbackBox = document.querySelector("#feedback");
        feedbackBox!.innerHTML = "";
        const textnode = document.createTextNode(text);
        feedbackBox?.appendChild(textnode);
    }

    function addDancerFeedback() {
        giveScreenReaderFeedback('dancer added');
        addDancer();
    }

    function updateSelectedDancer() {
        var element = (document.getElementById("dancerSelect")) as HTMLSelectElement;
        var opt = element.options[element.selectedIndex];

        setSelectedDancer(Number(opt.value));
    }

    function deleteDancerFeedback () {
        deleteDancer(Number(timeline.selectedDancerId));
        setSelectedDancer(null);
        giveScreenReaderFeedback('deleted');
    }

    function updateCurrentDancerName(e: React.ChangeEvent<HTMLInputElement>) {
        if (timeline.selectedDancerId === null) return;

        setDancerName(timeline.selectedDancerId, e.target.value);
    }

    function updateCurrentDancerPathColor(e: React.ChangeEvent<HTMLSelectElement>) {
        if (e.target.value === "") return;

        const dancer = timeline.dancers.find(dancer => dancer.id === timeline.selectedDancerId);
        if (dancer === undefined) return;

        setDancerPathAppearance(dancer.id, Number(e.target.value), dancer.pathNodeShape);
    }

    function updateCurrentDancerNodeShape(e: React.ChangeEvent<HTMLSelectElement>) {
        if (e.target.value === "") return;

        const dancer = timeline.dancers.find(dancer => dancer.id === timeline.selectedDancerId);
        if (dancer === undefined) return;

        setDancerPathAppearance(dancer.id, dancer.pathColor, Number(e.target.value));
    }

    function addNodeFeedback (atIndex: number) {
        if(timeline.selectedDancerId === null) return;
        giveScreenReaderFeedback('added');

        var selectedDancer = getSelectedDancer();
        var insertTime = 0;
        var insertPos = {x: 0, y: 0};

        if (selectedDancer.pathNodes.length === 0 || atIndex <= 0) {
            insertTime = 0;
        } else if (atIndex >= selectedDancer.pathNodes.length) {
            const prevDancerPathNode = selectedDancer.pathNodes[selectedDancer.pathNodes.length - 1];
            insertTime = prevDancerPathNode.time + 1;
        } else {
            const nextDancerPathNode = selectedDancer.pathNodes[atIndex];
            const prevDancerPathNode = selectedDancer.pathNodes[atIndex - 1];
            insertTime = (prevDancerPathNode.time + nextDancerPathNode.time) * 0.5;
            insertPos = {
                x: (prevDancerPathNode.pos.x + nextDancerPathNode.pos.x) / 2,
                y: (prevDancerPathNode.pos.y + nextDancerPathNode.pos.y) / 2,
            };
        }

        addDancerPathNode(timeline.selectedDancerId, insertTime, 0, insertPos);
    }

    function updatePathNode (nodeId: number) {
        if(timeline.selectedDancerId === null) return;

        var nodeName = ((document.getElementById(`node${nodeId}Name`)) as HTMLInputElement).value;
        var nodeLocationX = Number(((document.getElementById(`node${nodeId}LocationX`)) as HTMLInputElement).value);
        var nodeLocationY = Number(((document.getElementById(`node${nodeId}LocationY`)) as HTMLInputElement).value);
        var nodeDelay = Number(((document.getElementById(`node${nodeId}Delay`)) as HTMLInputElement).value);
        
        var nodeStart = undefined;
        var nodeStartInput = document.getElementById(`node${nodeId}Start`);
        if (nodeStartInput !== null) {
            nodeStart = Number((nodeStartInput as HTMLInputElement).value)
        }
        
        if (settings.units === Units.Metric) {
            nodeLocationX = metersToCentimeters(nodeLocationX);
            nodeLocationY = metersToCentimeters(nodeLocationY);
        }
        else {
            nodeLocationX = feetToCentimeters(nodeLocationX);
            nodeLocationY = feetToCentimeters(nodeLocationY);
        }
        
        setDancerPathNode(timeline.selectedDancerId, nodeId, nodeName, nodeStart, nodeDelay, {x: nodeLocationX, y: nodeLocationY});
    }
    
    function updatePathNodeDuration (nodeId: number) {
        if(timeline.selectedDancerId === null) return;

        var nodeDuration = Number(((document.getElementById(`node${nodeId}Duration`)) as HTMLInputElement).value);
        
        setDancerPathNodeDuration(timeline.selectedDancerId, nodeId, nodeDuration);
    }

    function deleteNodeFeedback (nodeId: number) {
        if(timeline.selectedDancerId === null) return;
        giveScreenReaderFeedback('deleted');

        deleteDancerPathNode(timeline.selectedDancerId, nodeId);
    }

    function updateDancerHeight (newHeight: number) {
        if(timeline.selectedDancerId === null) return;
        
        let feedback = '';
        if (settings.units === Units.Metric) {
            feedback = 'dancer height is ' + centimetersToMetersRounded(newHeight) + ' meters';
        }
        else {
            feedback = 'dancer height is ' + centimetersToFeetRounded(newHeight) + ' feet';
        }
        giveScreenReaderFeedback(feedback);

        setDancerHeight(timeline.selectedDancerId, newHeight);
    }

    function getSelectedDancer () {
        return timeline.dancers[timeline.dancers.findIndex(d => d.id === timeline.selectedDancerId)];
    }

    return (
        <div className='gridContainer'>
            <div className='sideBar' style={{display: 'flex', flexDirection: 'column'}}>
                <div style={{ display: 'flex', alignItems:'center', marginBottom: '20px' }}>
                    <img className='dancersIcon' alt='dancers' aria-hidden />
                    <h1 style={{ whiteSpace: 'nowrap', margin: '0' }}>DANCERS</h1>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', width:'100%' }} >
                        <button aria-label='add dancer' aria-controls='feedback' className='addHeaderButton' onClick={addDancerFeedback}/>
                    </div>
                </div>
                <div style={{ display: 'flex' }}>
                    <select id='dancerSelect' aria-label='dancer list' required value={timeline.selectedDancerId === null ? 'placeHolder' : timeline.selectedDancerId} className='dropdown' onChange={updateSelectedDancer}>
                        <option key={"placeHolder"} value='placeHolder' disabled>Select a dancer</option>
                        {timeline.dancers.map(dancer => 
                            <option key={dancer.id} value={dancer.id}>{dancer.name}</option>
                        )}
                    </select>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }} >
                        {timeline.selectedDancerId !== null
                            ? <button aria-label={`delete ${getSelectedDancer().name}`} aria-controls='feedback' className='removeFromDropdown' onClick={deleteDancerFeedback}/>
                            : <button aria-label={`no dancer selected`} aria-controls='feedback' className='removeFromDropdown-Disabled' disabled/>}
                    </div>
                </div>
                <hr className='divider' aria-hidden />
                { timeline.selectedDancerId !== null &&
                <>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <div className='subtitle' style={{ width: '30%' }}>Name</div>
                        <div className='elementValue' style={{ width:'70%' }} >
                            <input aria-label='edit dancer name' type='text' className='selectedOptionName' value={getSelectedDancer().name} onChange={updateCurrentDancerName}/>
                        </div>
                    </div>
                    <div className='responsiveContainer'>
                        <select id='dancerPathColor' aria-label={`${getSelectedDancer().name} path colour`} value={getSelectedDancer().pathColor} onChange={updateCurrentDancerPathColor} 
                            className={`dropdown-${DancerPathColor[getSelectedDancer().pathColor]}`} style={{ marginTop: '20px' }}>
                            {Object.keys(DancerPathColor).filter(color => isNaN(Number(color))).map((color, index) => 
                                <option key={color} value={index}>{color}</option>
                            )}
                        </select>
                        <select id='dancerPathShape' aria-label={`${getSelectedDancer().name} path shape`} value={getSelectedDancer().pathNodeShape} onChange={updateCurrentDancerNodeShape}
                            className={`dropdown-${DancerPathNodeShape[getSelectedDancer().pathNodeShape]}`} style={{ marginTop: '20px' }}>
                            {Object.keys(DancerPathNodeShape).filter(shape => isNaN(Number(shape))).map((shape, index) => 
                                <option key={shape} value={index}>{shape}</option>
                            )}
                        </select>
                    </div>
                    <MeasureInput label='Height' value={getSelectedDancer().height} min={100} max={300} onChangeFunc={updateDancerHeight} ariaLabel='dancer height' />
                    <hr className='divider' aria-hidden/>
                    <div style={{ flexGrow: 1, overflowY: 'auto', padding: '10px', backgroundColor: '#191919'/*scrollbarGutter: 'stable'*/ }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                            <button aria-label='add node before first node' className='addNodeButton' onClick={() => addNodeFeedback(0)}>Add Node</button>
                        </div>
                        { getSelectedDancer().pathNodes.map((node, index) =>
                            <>
                                <div style={{ 
                                    outline: getSelectedDancer().id === timeline.selectedDancerPathNode?.dancerId && node.id === timeline.selectedDancerPathNode.pathNodeId ? "solid white 1px" : "none",
                                    outlineOffset: 5,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: '10px' }}>
                                        <input id={`node${node.id}Name`} aria-label='edit node name' type='text' className='inputBox' value={node.name} style={{ marginLeft: '0px', textAlign: 'left', textIndent: '10px' }} onChange={() => updatePathNode(node.id)}/>
                                        <hr className='divider' aria-hidden style={{ width: '50%' }} />
                                        <button aria-label={`delete ${node.name}`} aria-controls='feedback' className='deleteNodeButton' onClick={() => deleteNodeFeedback(node.id)}/>
                                    </div>
                                    <div className='locationContainer'>
                                        <div className='locationLabel'>Location (X,Y)</div>
                                        <div className='locationValue'>
                                            <input id={`node${node.id}LocationX`} type='number' step='0.01' className='inputBox' style={{ width: '80px' }} onChange={() => updatePathNode(node.id)}
                                                aria-label={settings.units === Units.Metric ? `node ${node.id} location X meters` : `node ${node.id} location X feet`} aria-live="assertive"
                                                value={settings.units === Units.Metric ? centimetersToMetersRounded(node.pos.x) : centimetersToFeetRounded(node.pos.x)}/>
                                            ,
                                            <input id={`node${node.id}LocationY`} type='number' step='0.01' className='inputBox' style={{ width: '80px' }} onChange={() => updatePathNode(node.id)}
                                                aria-label={settings.units === Units.Metric ? `node ${node.id} location Y meters` : `node ${node.id} location Y feet`} aria-live="assertive"
                                                value={settings.units === Units.Metric ? centimetersToMetersRounded(node.pos.y) : centimetersToFeetRounded(node.pos.y)}/>
                                            { settings.units === Units.Metric ? <>m</> : <>ft</> }
                                        </div>
                                    </div>
                                    { index === 0 &&
                                        <div style={{ display: 'flex', alignItems:'center', width: '100%', marginTop: '10px' }}>
                                            <div className='subtitle' style={{ width: '50%' }}>Node Start</div>
                                            <div className='elementValue' style={{ width:'50%' }} >
                                                <input id={`node${node.id}Start`} type='number' min='0' step='0.01' className='inputBox' style={{ marginRight: '10px' , width: '6em' }} onChange={() => updatePathNode(node.id)}
                                                    aria-label={`start time of ${node.name} in seconds`}
                                                    aria-live="assertive"
                                                    value={(node.time).toFixed(2)}/>
                                                <label className='subtitle'>s</label>
                                            </div>
                                        </div>
                                    }
                                    { index < getSelectedDancer().pathNodes.length - 1 &&
                                        <div style={{ display: 'flex', alignItems:'center', width: '100%', marginTop: '10px' }}>
                                            <div className='subtitle' style={{ width: '50%' }}>Node Duration</div>
                                            <div className='elementValue' style={{ width:'50%' }} >
                                                <input id={`node${node.id}Duration`} type='number' min='0' step='0.01' className='inputBox' style={{ marginRight: '10px' , width: '6em' }} onChange={() => updatePathNodeDuration(node.id)}
                                                    aria-label={`duration from ${node.name} to next node in seconds`}
                                                    aria-live="assertive"
                                                    value={(getSelectedDancer().pathNodes[index + 1].time - node.time).toFixed(2)}/>
                                                <label className='subtitle'>s</label>
                                            </div>
                                        </div>
                                    }
                                    <div style={{ display: 'flex', alignItems:'center', width: '100%', marginTop: '10px' }}>
                                        <div className='subtitle' style={{ width: '50%' }}>Node Delay</div>
                                        <div className='elementValue' style={{ width:'50%' }} >
                                            <input id={`node${node.id}Delay`} type='number' min='0' step='0.01' className='inputBox' style={{ marginRight: '10px' , width: '6em' }} onChange={() => updatePathNode(node.id)}
                                                aria-label={`${node.name} delay in seconds`}
                                                aria-live="assertive"
                                                value={(node.delay).toFixed(2)}/>
                                            <label className='subtitle'>s</label>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0px 10px 0px' }}>
                                    <button className='addNodeButton' onClick={() => addNodeFeedback(index + 1)}
                                        aria-label={ index === getSelectedDancer().pathNodes.length - 1 ? 'Add node to end of path' : `Add node between ${node.name} and ${getSelectedDancer().pathNodes[index + 1].name}` }>
                                            Add Node
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </>
                }
            </div>
            <div className='stage'>
                <StageCanvas mode={StageCanvasMode.DancerPaths}/>
            </div>
            <div className='timeline'>
                <Timeline />
            </div>
        </div>
    )
};

export default Dancers;